import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import clsx from 'clsx';
import { useConfirmUser } from '../../queries/Accounts';
import { useTheme } from '../../contexts/ThemeContext';
import ThemeToggle from '../../components/ThemeToggle';

/**
 * Password rules enforced by ASP.NET Identity on the server. Listed here so a
 * new user can satisfy them first time instead of discovering them one failed
 * submit at a time.
 */
const PASSWORD_RULES = [
    { label: 'At least 8 characters', test: (value: string) => value.length >= 8 },
    { label: 'An uppercase letter', test: (value: string) => /[A-Z]/.test(value) },
    { label: 'A lowercase letter', test: (value: string) => /[a-z]/.test(value) },
    { label: 'A number', test: (value: string) => /[0-9]/.test(value) },
    {
        label: 'A symbol (e.g. ! ? @ #)',
        test: (value: string) => /[^a-zA-Z0-9]/.test(value),
    },
    {
        label: '4 different characters',
        test: (value: string) => new Set(value).size >= 4,
    },
];

function WarningIcon({ className }: { className?: string }) {
    return (
        <svg
            className={className}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
        </svg>
    );
}

function TickIcon({ className }: { className?: string }) {
    return (
        <svg
            className={className}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
        >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
        </svg>
    );
}

export default function Verification(props: RouteComponentProps) {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [password, setPassword] = useState('');
    const [userId, setUserId] = useState('');
    const [code, setCode] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [invalidRequest, setInvalidRequest] = useState(false);

    const confirmUser = useConfirmUser();
    const { theme } = useTheme();

    useEffect(() => {
        const search = props.location?.search;

        // Read by name rather than by position. The previous version split on
        // '&' and trusted parts[0] to be userId and parts[1] to be code, so it
        // depended on the server emitting the parameters in one exact order and
        // would silently mis-parse anything else - including the extra name
        // parameters the link now carries.
        const params = search ? new URLSearchParams(search) : undefined;
        const id = params?.get('userId');
        const confirmationCode = params?.get('code');

        if (!params || !id || !confirmationCode) {
            setInvalidRequest(true);
            setErrorMessage('Invalid Request');
            return;
        }

        setUserId(id);
        setCode(confirmationCode);
        // Prefilled when the admin already captured them, so submitting this
        // form cannot blank out a name that was already on record.
        setFirstName(params.get('firstName') || '');
        setLastName(params.get('lastName') || '');
        // eslint-disable-next-line react/destructuring-assignment
    }, [props.location?.search]);

    useEffect(() => {
        if (confirmUser.error) {
            setErrorMessage(confirmUser.error || 'Failed to verify your account');
        }
    }, [confirmUser.error]);

    const passwordMatch = () => password && confirmPassword && password === confirmPassword;

    const postUserDto = (event?: React.FormEvent) => {
        event?.preventDefault();
        setErrorMessage('');
        if (!passwordMatch()) {
            setErrorMessage('Please make sure the passwords match');
            return;
        }
        confirmUser.execute({
            userId,
            code,
            user: { firstName, lastName, password },
        });
    };

    const logoSrc = theme === 'dark' ? '/logo-dark.png' : '/logo-light.png';
    const pageBg = theme === 'dark' ? '#2d3b46' : '#f1f2f4';
    const cardShadow =
        theme === 'dark'
            ? '0 1px 2px rgba(0,0,0,0.4), 0 20px 40px -12px rgba(0,0,0,0.5)'
            : '0 1px 2px rgba(15,15,15,0.04), 0 2px 8px rgba(15,15,15,0.04), 0 24px 48px -16px rgba(15,15,15,0.14)';

    /** Page shell shared by all three states, matching the sign-in screen. */
    const shell = (children: React.ReactNode, wide?: boolean) => (
        <div
            className="min-h-screen w-full flex flex-col items-center justify-center px-4 py-10"
            style={{ backgroundColor: pageBg, transition: 'background-color 0.2s ease' }}
        >
            <div className="fixed top-4 right-4 sm:top-6 sm:right-6">
                <ThemeToggle />
            </div>

            <div className="flex flex-col items-center mb-8">
                <div className="flex items-center gap-2">
                    <img src={logoSrc} alt="Raubex BIC" className="h-8 w-auto" />
                    <div className="text-2xl font-bold">
                        <span className="text-orange-500">B</span>
                        <span className="text-foreground">IC</span>
                    </div>
                </div>
                <p className="text-sm text-foreground-secondary mt-1">
                    Building Information Control
                </p>
            </div>

            <div
                className={clsx(
                    'w-full bg-white rounded-2xl px-8 py-8',
                    wide ? 'max-w-md' : 'max-w-sm'
                )}
                style={{ boxShadow: cardShadow }}
            >
                {children}
            </div>

            <div className="text-xs text-foreground-secondary mt-8">
                © {new Date().getFullYear()} Raubex Building Cape
            </div>
        </div>
    );

    const errorBlock = errorMessage && (
        <div className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 border border-red-100 px-3.5 py-2.5 text-sm text-red-600">
            <WarningIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{errorMessage}</span>
        </div>
    );

    if (invalidRequest) {
        return shell(
            <>
                <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
                        <WarningIcon className="w-6 h-6 text-red-500" />
                    </div>
                </div>
                <h1 className="text-lg font-bold text-gray-900 text-center mb-2">
                    This link isn&apos;t valid
                </h1>
                <p className="text-sm text-gray-500 text-center">
                    The registration link is incomplete or has already been used. Ask your
                    administrator to send you a new one.
                </p>
            </>
        );
    }

    if (confirmUser.success) {
        return shell(
            <>
                <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
                        <TickIcon className="w-6 h-6 text-green-600" />
                    </div>
                </div>
                <h1 className="text-lg font-bold text-gray-900 text-center mb-2">
                    Your account is ready
                </h1>
                <p className="text-sm text-gray-500 text-center mb-5">
                    You can now sign in to BIC with your new password.
                </p>
                <a
                    href="/login"
                    className="w-full flex items-center justify-center rounded-full bg-orange-500 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                >
                    Go to sign in
                </a>
            </>
        );
    }

    const fieldClass =
        'w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-full text-sm text-gray-900 placeholder-gray-400 transition-colors focus:outline-none focus:bg-white focus:border-gray-300';
    const labelClass = 'block text-sm font-bold text-gray-900 mb-1.5';

    const confirmMismatch = confirmPassword !== '' && password !== confirmPassword;

    return shell(
        <>
            <h1 className="text-lg font-bold text-gray-900 mb-1">Set up your account</h1>
            <p className="text-sm text-gray-500 mb-5">
                Confirm your details and choose a password to finish registering.
            </p>

            <form onSubmit={postUserDto}>
                <label htmlFor="verify-first-name" className={labelClass}>
                    First name
                </label>
                <input
                    id="verify-first-name"
                    name="firstName"
                    type="text"
                    required
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                    className={`${fieldClass} mb-4`}
                />

                <label htmlFor="verify-last-name" className={labelClass}>
                    Last name
                </label>
                <input
                    id="verify-last-name"
                    name="lastName"
                    type="text"
                    required
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                    className={`${fieldClass} mb-4`}
                />

                <label htmlFor="verify-password" className={labelClass}>
                    Password
                </label>
                <input
                    id="verify-password"
                    name="password"
                    type="password"
                    required
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className={`${fieldClass} mb-3`}
                />

                {/* Live checklist rather than a server round-trip per attempt. */}
                <ul className="mb-4 space-y-1">
                    {PASSWORD_RULES.map((rule) => {
                        const met = rule.test(password);
                        return (
                            <li
                                key={rule.label}
                                className={clsx(
                                    'flex items-center gap-1.5 text-xs',
                                    met ? 'text-green-600' : 'text-gray-400'
                                )}
                            >
                                <TickIcon className="w-3 h-3 flex-shrink-0" />
                                {rule.label}
                            </li>
                        );
                    })}
                </ul>

                <label htmlFor="verify-confirm-password" className={labelClass}>
                    Confirm password
                </label>
                <input
                    id="verify-confirm-password"
                    name="confirmPassword"
                    type="password"
                    required
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    className={clsx(fieldClass, confirmMismatch && 'border-red-300')}
                />
                {confirmMismatch && (
                    <p className="text-xs text-red-600 mt-1.5">Passwords do not match.</p>
                )}

                {errorBlock}

                <button
                    type="submit"
                    disabled={confirmUser.loading}
                    className={clsx(
                        'w-full flex items-center justify-center gap-2 rounded-full bg-orange-500 py-3 text-sm font-semibold text-white transition-opacity mt-5',
                        confirmUser.loading ? 'opacity-40 cursor-not-allowed' : 'hover:opacity-90'
                    )}
                >
                    {confirmUser.loading ? 'Creating your account...' : 'Create account'}
                </button>
            </form>
        </>,
        true
    );
}
